import React, { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

type Variant = "primary" | "secondary" | "link" | "follow";
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean;
  variant: Variant;
}
const sharedButtonClassName =
  "inline-flex text-sm font-semibold focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-brand-deepaqua justify-center rounded-lg px-3 py-2";
export const primaryButtonClassName =
  "bg-brand-deepaqua-light text-white hover:bg-brand-deepaqua-lighter " +
  sharedButtonClassName;
export const secondaryButtonClassName =
  "bg-white text-brand-deepaqua-light ring-1 ring-inset ring-brand-deepaqua hover:bg-brand-gray hover:ring-brand-gray " +
  sharedButtonClassName;

export const linkButtonClassName =
  "text-white hover:text-brand-seafoam font-semibold underline ";

export const disabledButtonClassName =
  "bg-[#EBF1F2] text-[#B6CACF] hover:bg-[#EBF1F2] hover:text-[#B6CACF]";

const Button = forwardRef(
  (
    { className, children, isLoading, variant, ...rest }: ButtonProps,
    ref: React.Ref<HTMLButtonElement>,
  ) => {
    return (
      <button
        type="button"
        className={twMerge(
          variant === "primary" && primaryButtonClassName,
          variant === "secondary" && secondaryButtonClassName,
          variant === "secondary" && rest.disabled && "ring-0",
          variant === "link" && linkButtonClassName,
          rest.disabled && disabledButtonClassName,
          className,
        )}
        disabled={isLoading || rest.disabled}
        {...rest}
        ref={ref}
      >
        {isLoading && (
          <svg
            className={twMerge(
              "-ml-1 animate-spin",
              variant === "primary" && "text-white",
              variant === "secondary" && "text-brand-deepaqua-light",
              ["primary", "secondary", "link"].includes(variant) &&
                "mr-3 h-5 w-5",
            )}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className={twMerge(
                "opacity-25",
                variant === "primary" && "stroke-current",
                (variant === "secondary" || variant === "follow") &&
                  "stroke-n-purple-700",
              )}
              cx="12"
              cy="12"
              r="10"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        )}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
